import { ICliJob } from 'cli/types';
import { RequestReturnType } from 'core/enums';
import { mapRecordFromServer, mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, GetOneResponse } from 'core/types';
import {
  getQueryString,
  getQueryStringToSnakeCase,
  objectKeysCamelToSnakeCase,
  objectKeysSnakeCaseToCamelCase,
} from 'core/utils';
import {
  IAddBomPartsToNewQuoteData,
  IAddBomPartsToQuoteData,
  IAddFinderPartsToNewQuoteData,
  IAddFinderPartsToQuoteData,
  IAddPartsToQuoteData,
  IAttachedVendorQuote,
  IBaseQuote,
  IBulkSendAsSpaResponse,
  ICreateProspectQuoteResponse,
  ICustomerQuote,
  ICustomerQuoteListByCustomerPayload,
  IProspectContactInfoResponse,
  IProspectQuotePayload,
  IQuoteAttachedFilesResponse,
  IQuoteExtraData,
  IQuoteListPayload,
  ISendToFilenetResponse,
  IUpdateCustomerQuoteContactGroupAddress,
  IVendorQuote,
  IVendorQuoteListByVendorPayload,
  IVendorQuoteListRequestPayload,
  IVendorQuoteMainInfoPayload,
  IVendorQuoteMainInfoResponse,
} from 'quotes/types';

export const getQuote = async (
  requestPayload: { quoteId: string },
  signal?: AbortSignal
): Promise<GetOneResponse<IBaseQuote, IQuoteExtraData>> => {
  const { data } = await partfinitiClient.get(
    `/quote/details?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordFromServer<IBaseQuote, IQuoteExtraData>(data?.quote, data?.success, {
    expiredCostingAboveThreshold: data?.expired_costing_above_threshold,
    expiredCostingLinesCount: data?.expired_costing_lines_count,
    pendingCliJobs: data?.pending_cli_jobs,
  });
};

export const getCustomerQuoteByCustomer = async (
  requestPayload: ICustomerQuoteListByCustomerPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<ICustomerQuote>> => {
  const { data } = await partfinitiClient.get(
    `/quote/customer/get-by-customer?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordFromServer<ICustomerQuote>(data.quote);
};

export const getCustomerQuotes = async (
  requestPayload: IQuoteListPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<ICustomerQuote, ICliJob>> => {
  const { data } = await partfinitiClient.get(
    `/quote/customer/quotes?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<ICustomerQuote, ICliJob>(
    data?.data,
    data?.recordsFiltered,
    data?.totals,
    data?.get_orders_info_from_core_cli_job
  );
};

export const getVendorQuoteByVendor = async (
  requestPayload: IVendorQuoteListByVendorPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IVendorQuote>> => {
  const { data } = await partfinitiClient.get(
    `/quote/vendor/get-by-vendor?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordFromServer<IVendorQuote>(data.quote);
};

export const getVendorQuotes = async (requestPayload: IQuoteListPayload, signal?: AbortSignal) => {
  const { data } = await partfinitiClient.get(
    `/quote/vendor/quotes?${getQueryString(objectKeysCamelToSnakeCase(requestPayload))}`,
    signal
  );

  return mapRecordsFromServer<IVendorQuote>(data?.data, data?.recordsFiltered, data?.totals);
};

export const getAttachedVendorQuotes = async (
  requestPayload: IVendorQuoteListRequestPayload,
  signal?: AbortSignal
) => {
  const { data } = await partfinitiClient.get(
    `/quote/attached-vendor-quote-list/quotes?${getQueryString(
      objectKeysCamelToSnakeCase(requestPayload)
    )}`,
    signal
  );

  return mapRecordsFromServer<IAttachedVendorQuote>(data?.data, data?.recordsFiltered);
};

export const getQuoteFiles = async (
  requestPayload: { filters: { quoteId: number } },
  signal?: AbortSignal
): Promise<GetManyResponse<IQuoteAttachedFilesResponse>> => {
  const { data } = await partfinitiClient.get(
    `/quote/quote-file-association-list/list?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IQuoteAttachedFilesResponse>(data?.data, data?.recordsFiltered);
};

export const exportQuote = async (data): Promise<ICliJob> =>
  (await partfinitiClient.postForm(`/quote/${data.options.quoteType}/generate-export-file`, data))
    .cliJob;

export const exportBulkVendorQuoteSkuParts = async (data: {
  quoteIds: number[];
  options: { partExportType: string; fileType: string; quoteType: string };
}): Promise<ICliJob> =>
  (await partfinitiClient.postForm('/quote/vendor/generate-bulk-export-file', data)).cliJob;

// Enqueues the async "Send as SPA" job; the BE regenerates the export from the quote's
// persisted Attributes, so no options are sent. postForm snake_cases quoteId -> quote_id and
// localDate -> local_date (the user's calendar day, YYYY-MM-DD; a resend ends the live agreement
// on that day).
export const sendVendorQuoteAsSpa = async (data: {
  quoteId: number;
  localDate: string;
}): Promise<ICliJob> => (await partfinitiClient.postForm('/quote/vendor/send-as-spa', data)).cliJob;

export const sendBulkVendorQuoteAsSpa = async (data: {
  quoteIds: number[];
  localDate: string;
}): Promise<IBulkSendAsSpaResponse> =>
  partfinitiClient.postForm('/quote/vendor/send-bulk-as-spa', data);

export const upsertCustomerQuote = async (data: { quote: Record<string, unknown> }) =>
  partfinitiClient.postForm('/quote/customer/save', data, { includeNullValues: true });

// Session-authed twin of the add-in's Bearer prospect endpoint: creates the prospect contact,
// its company when the email domain matches none, and an empty customer quote in one call.
export const createProspectQuote = async (
  data: IProspectQuotePayload
): Promise<ICreateProspectQuoteResponse> =>
  partfinitiClient.postForm('/quote/prospect/create', data);

// Previews what the email already resolves to server-side. A returned contactGroup means the
// create call will attach to that company and discard the submitted companyName.
export const getProspectContactInfo = async (
  requestPayload: { email: string },
  signal?: AbortSignal
): Promise<IProspectContactInfoResponse> => {
  const { data } = await partfinitiClient.get(
    `/quote/prospect/contact-info?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data) as IProspectContactInfoResponse;
};

export const upsertCustomerQuotes = async (data: {
  quotes: Partial<ICustomerQuote>[];
  returnType?: RequestReturnType;
}) => partfinitiClient.postForm('/quote/customer/save-bulk', data);

export const upsertVendorQuotes = async (data: {
  quotes: Partial<IVendorQuote>[];
  returnType?: RequestReturnType;
}) => partfinitiClient.postForm('/quote/vendor/save-bulk', data);

export const upsertVendorQuote = async (data: Partial<IVendorQuote>) =>
  partfinitiClient.postForm('/quote/vendor/save', data, { includeNullValues: true });

export const saveVendorQuoteMainInfo = async (
  data: IVendorQuoteMainInfoPayload
): Promise<IVendorQuoteMainInfoResponse> =>
  partfinitiClient.postForm('/quote/vendor/save', data, { includeNullValues: true });

export const upsertCustomerQuoteAddress = async (
  data: Partial<IUpdateCustomerQuoteContactGroupAddress>
) =>
  partfinitiClient.postForm('/quote/customer/save-address', data, {
    includeNullValues: true,
  });

export const upsertQuoteToQuoteAssociation = async (data) =>
  partfinitiClient.postForm('/quote/quote-association-edit/save-bulk', data);

export const upsertQuoteToProjectAssociation = async (data) =>
  partfinitiClient.postForm('/project_v2/quote-association-edit/save-bulk', data);

export const upsertQuoteNote = async (data) =>
  partfinitiClient.postForm('/quote/note/save', data, { includeNullValues: true });

export const detachVendorQuote = async (data) =>
  partfinitiClient.postForm('/quote/quote-disaffiliation-edit/save-bulk', data);

export const reattachVendorQuote = async (data) =>
  partfinitiClient.postForm('/quote/quote-disaffiliation-edit/remove', data);

export const addPartsToCustomerQuote = async (data: IAddPartsToQuoteData) =>
  (await partfinitiClient.postForm('/quote/customer/add-parts-to-customer-quote', data)).cliJob;

export const addPartsToVendorQuote = async (data: IAddPartsToQuoteData) =>
  (await partfinitiClient.postForm('/quote/vendor/add-parts-to-vendor-quote', data)).cliJob;

export const addBomPartsToCustomerQuote = async (data: IAddBomPartsToQuoteData) =>
  (await partfinitiClient.postForm('/quote/customer/add-parts-from-bom', data)).cliJob;

export const addBomPartsToNewCustomerQuote = async (data: IAddBomPartsToNewQuoteData) =>
  (await partfinitiClient.postForm('/quote/customer/create-from-bom', data)).cliJob;

export const addFinderPartsToCustomerQuote = async (data: IAddFinderPartsToQuoteData) =>
  (await partfinitiClient.postForm('/quote/customer/add-parts-from-finder', data)).cliJob;

export const addFinderPartsToNewCustomerQuote = async (data: IAddFinderPartsToNewQuoteData) =>
  (await partfinitiClient.postForm('/quote/customer/create-from-finder', data)).cliJob;

export const addBomPartsToVendorQuote = async (data: IAddBomPartsToQuoteData) =>
  (await partfinitiClient.postForm('/quote/vendor/add-parts-from-bom', data)).cliJob;

export const addBomPartsToNewVendorQuote = async (data: IAddBomPartsToNewQuoteData) =>
  (await partfinitiClient.postForm('/quote/vendor/create-from-bom', data)).cliJob;

export const upsertQuoteVersionData = async (data) =>
  partfinitiClient.postForm('/quote/version/save-version-data', data, { includeNullValues: true });

export const submitCustomerOrder = async (data) =>
  (await partfinitiClient.postForm('/quote/customer/submit-order', data)).cliJob;

export const submitVendorOrder = async (data) =>
  partfinitiClient.postForm('/quote/vendor/submit-order', data);

export const sendVendorQuoteToVendor = async (data: { quoteId: number }) =>
  partfinitiClient.postForm('/quote/vendor/send-to-vendor', data);

export const sendVendorQuoteToFilenet = async (data: {
  quoteId: number;
}): Promise<ISendToFilenetResponse> =>
  partfinitiClient.postForm('/quote/vendor/send-to-filenet', data);

export const createCustomerOrderFromQuote = async (data) =>
  (
    await partfinitiClient.postForm('/quote/customer/create-order-from-quote', data, {
      includeNullValues: true,
    })
  ).cliJob;

export const createVendorOrderFromQuote = async (data) =>
  (await partfinitiClient.postForm('/quote/vendor/create-order-from-quote', data)).cliJob;

export const attachQuoteFile = async (data) =>
  partfinitiClient.postForm('/quote/quote-file-association-edit/save', data);

export const detachQuoteFile = async (data) =>
  partfinitiClient.postForm('/quote/quote-file-association-edit/remove', data);
